import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { DateTime } from 'luxon';

import * as XLSX from 'xlsx';
import Permissions from '../auth/Permissions';

import BreadCrumbContainer from '../common/breadcrumbContainer/breadcrumbContainer';
import ArchiveModal from '../common/modals/ArchiveModal';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';

import MaterialsHeader from './MaterialsHeader';
import MaterialsTable from './MaterialsTable';
import MaterialDrawer from './MaterialDrawer';
import MaterialFolderAddDrawer from './MaterialFolderAddDrawer';
import MaterialBulkChangeDrawer from './MaterialBulkChangeDrawer';
import MaterialDestinationDrawer from './MaterialDestinationDrawer';
import MaterialPermsDrawer from './MaterialPermsDrawer';

import {
  formatExportedMaterial,
  getFolderDescendants,
  getMaterialPath,
  getRelevantDeleteWarning,
  inCurrentFolder,
  prepareMaterialLocations,
} from './materialsHelpers';

import {
  createMaterial,
  getMaterials,
  archiveMaterial,
  updateMaterial,
  deleteMaterial,
  bulkUpdateMaterials,
  createMaterialsFolder,
  updateMaterialsFolder,
  moveMaterials,
  copyMaterials,
  uploadMaterials,
  massArchive,
  massDelete,
} from './state/materials.actions';
import { getEquipment } from '../equipment/state/equipment.actions';
import { getCardLinks, closeFormDrawer } from '../boards/state/boards.actions';
import { getVendors } from '../contacts/vendors/state/vendors.actions';
import FormDetailView from '../forms/CompletedForms/FormDetailView';

import { convertPascalCaseToCamelCase } from '../forms/FormBuilder/FormFields/TimeEntryTable/TimeEntryTable.helpers';
import FilterDropdown from '../common/FilterDropdown';
import { sortByObjProperty } from '../common/helpers';
import { getIdMap } from '../helpers/helpers';
import {
  getMissingDivisionIds,
  haveSameDivisions,
  isInDivisions,
} from '../helpers/divisionHelpers';
import { INITIAL_SORT_FILTERS } from './materials.constants';

import { getCostcodeByIds } from '../costcodes/state/costcodes.actions';
import { getQuickbooksIntegrations } from '../settings/state/settings.actions';

const INITIAL_CRUMBS = [{
  text: 'Materials',
  icon: 'materials',
}];

const filters = [[
  [
    {
      title: 'Type',
      key: 'type',
      children: [
        {
          title: 'Material',
          key: 'Material',
        },
        {
          title: 'Assembly',
          key: 'Assembly',
        },
      ],
    },
  ],
]];

export default function Materials() {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    state: {
      targetId: locationStateId,
    } = {},
  } = useLocation();

  const materials = useSelector((state) => state.materials.materials);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const globalMaterialLocations = useSelector((state) => (
    state.globalMaterialLocations.globalMaterialLocations
  ));
  const equipment = useSelector((state) => state.equipment.equipment);
  const {
    visible: formDrawerVisible = false,
  } = useSelector((state) => state.boards.formDrawerConfig);
  const vendors = useSelector((state) => state.vendors.vendors);

  const costcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);
  const globalMaterialLocationsMap = useMemo(
    () => getIdMap(globalMaterialLocations),
    [globalMaterialLocations],
  );
  const equipmentMap = useMemo(() => getIdMap(equipment), [equipment]);
  const vendorMap = useMemo(() => vendors, [vendors]);

  const [crumbs, setCrumbs] = useState(INITIAL_CRUMBS);
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState();
  const [isDisplay, setIsDisplay] = useState(false);
  const [lastCreate, setLastCreate] = useState(DateTime.local().toMillis());
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [bulkChangeType, setBulkChangeType] = useState();
  const [bulkChangeCostType, setBulkChangeCostType] = useState();
  const [selectedColumns, setSelectedColumns] = useState();
  const [showFolderAdd, setShowFolderAdd] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState({});
  const [loading, setLoading] = useState(false);
  const [showDestinationDrawer, setShowDestinationDrawer] = useState({
    visible: false,
    isCopy: false,
  });
  const [selectedPermsMaterial, setSelectedPermsMaterial] = useState(false);
  const [isAssembly, setIsAssembly] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    type: new Set([
      'Material',
      'Assembly',
    ]),
  });
  const [statusFilters, setStatusFilters] = useState(INITIAL_SORT_FILTERS.filters);
  const [exportLoading, setExportLoading] = useState(false);

  const onShowPerms = useCallback((permsMaterial) => setSelectedPermsMaterial(permsMaterial), []);
  const onHidePerms = useCallback(() => setSelectedPermsMaterial(), []);

  const filterIsAtDefault = useMemo(() => {
    const {
      type = new Set(),
    } = activeFilters;
    return type.has('Material')
      && type.has('Assembly');
  }, [activeFilters]);

  const onFilter = useCallback((checkedFilters, filterCategory) => {
    // Need to filter out header values
    setActiveFilters({
      ...activeFilters,
      [filterCategory]: new Set(checkedFilters),
    });
  }, [activeFilters]);

  const openMoveDrawer = useCallback(() => (
    setShowDestinationDrawer({ visible: true, isCopy: false })
  ), []);
  const closeMoveDrawer = useCallback(() => (
    setShowDestinationDrawer({ visible: false, isCopy: false })
  ), []);
  const openCopyDrawer = useCallback(() => (
    setShowDestinationDrawer({ visible: true, isCopy: true })
  ), []);
  const closeCopyDrawer = useCallback(() => (
    setShowDestinationDrawer({ visible: false, isCopy: true })
  ), []);
  const closeDrawer = useCallback(() => {
    setShowDrawer(false);
    setSelectedMaterial();
    setIsAssembly();
  }, []);

  const onAddClicked = useCallback((isAddAssembly = false) => {
    setSelectedMaterial();
    setShowDrawer(true);
    setIsDisplay(false);
    setIsAssembly(isAddAssembly);
  }, []);

  const onAddFolderClicked = useCallback(() => setShowFolderAdd(true), []);
  const closeFolderAdd = useCallback(() => {
    setSelectedFolder({});
    setShowFolderAdd(false);
  }, []);
  const onFolderEdit = useCallback((record) => {
    setSelectedFolder(record);
    setShowFolderAdd(true);
  }, []);

  const onFolderSubmit = useCallback(async (folderData) => {
    const { id: groupId = null } = crumbs[crumbs.length - 1];

    if (!selectedFolder.id) {
      if (await dispatch(createMaterialsFolder({ ...folderData, groupId }))) {
        setShowFolderAdd(false);
      }
      setLastCreate(DateTime.local().toMillis());
      return;
    }

    // Edit
    const { name: oldName, divisionIds: oldDivisionIds = [] } = selectedFolder;
    const newDivisionIds = folderData.divisionIds ?? [];
    const update = {};
    if (folderData.name !== oldName) update.name = folderData.name;
    if (!haveSameDivisions(newDivisionIds, oldDivisionIds)) {
      update.divisionIds = newDivisionIds;
    }

    const submit = async () => {
      if (
        Object.keys(update).length === 0
        || await dispatch(updateMaterialsFolder(selectedFolder.id, update))
      ) {
        setShowFolderAdd(false);
      }
      setLastCreate(DateTime.local().toMillis());
    };

    /*
      A material's divisions must fit inside its parent folder's, so removing a division
      from a folder strips it from everything underneath. Warn before that happens.
    */
    const removedDivisionIds = getMissingDivisionIds(oldDivisionIds, newDivisionIds);
    const affectedDescendants = removedDivisionIds.length
      ? getFolderDescendants(selectedFolder.id, materials).filter((descendant) => (
        getMissingDivisionIds(descendant.divisionIds, newDivisionIds).length > 0
      ))
      : [];

    if (!affectedDescendants.length) {
      await submit();
      return;
    }

    const { length: affectedCount } = affectedDescendants;
    CustomConfirmModal({
      title: 'Remove divisions from this folder?',
      content: (
        <p>
          {affectedCount === 1
            ? '1 item inside this folder'
            : `${affectedCount} items inside this folder`}
          {' will lose the divisions you removed. This cannot be undone automatically.'}
        </p>
      ),
      okText: 'Remove',
      onOk: submit,
    });
  }, [dispatch, selectedFolder, crumbs, materials]);

  const onAddMaterial = useCallback(async ({
    values,
    locations,
    shouldAutoCalcCost,
    shouldAutoCalcLabourCost,
    assemblyMaterials,
    isAssembly: payloadIsAssembly,
  }) => {
    const { id: groupId = null } = crumbs[crumbs.length - 1];
    const newValues = {
      ...values,
      name: values.name?.trimStart(),
      groupId,
      markup: values.markup ? values.markup / 100 : values.markup,
      shouldAutoCalcCost: !!shouldAutoCalcCost,
      shouldAutoCalcLabourCost: !!shouldAutoCalcLabourCost,
      isAssembly: !!payloadIsAssembly,
      assemblyMaterials,
      labourMarkup: values.labourMarkup ? values.labourMarkup / 100 : values.labourMarkup,
    };
    if (selectedMaterial && selectedMaterial.id) {
      const locationPayload = prepareMaterialLocations({
        newLocations: locations,
        oldLocations: selectedMaterial.locations,
      });
      delete newValues.groupId; // Can only update group via "Move" API
      delete newValues.isAssembly;

      const payload = {
        ...newValues,
        ...locationPayload,
      };

      if (await dispatch(updateMaterial(selectedMaterial.id, payload))) {
        setShowDrawer(false);
        setIsAssembly();
        setSelectedMaterial();
      }
    } else {
      const { createdLocations } = prepareMaterialLocations({
        newLocations: locations,
      });

      const payload = {
        ...newValues,
        createdLocations,
      };
      if (await dispatch(createMaterial(payload))) {
        setShowDrawer(false);
        setIsAssembly();
      }
      return;
    }
    setLastCreate(DateTime.local().toMillis());
  }, [selectedMaterial, crumbs]);

  const onMassUpload = useCallback(async (massUploadData = []) => {
    if (massUploadData.length === 0) {
      setShowDrawer(false);
      setIsAssembly();
      return;
    }
    const { id: groupId = null } = crumbs[crumbs.length - 1];
    const parsedMaterials = massUploadData.map((mat) => ({
      ...mat,
      groupId,
    }));
    if (await dispatch(uploadMaterials({
      materials: parsedMaterials,
    }))) {
      setShowDrawer(false);
      setIsAssembly();
    }
  }, [crumbs]);

  const onMoveMaterial = useCallback(async (newLocation) => {
    const payload = {
      materialIds: selectedRecords.map((material) => material.id),
      groupId: newLocation,
    };

    setLoading(true);
    const res = await dispatch(moveMaterials(payload));
    setLoading(false);
    if (res) {
      closeMoveDrawer();
      setSelectedRecords([]);
      setLoading(false);
    }
  }, [dispatch, selectedRecords]);

  const onCopyMaterial = useCallback(async (copyDestinationId) => {
    const payload = {
      materialIds: selectedRecords.map((material) => material.id),
      groupId: copyDestinationId,
    };

    setLoading(true);
    const res = await dispatch(copyMaterials(payload));
    setLoading(false);
    if (res) {
      closeCopyDrawer();
      setSelectedRecords([]);
    }
  }, [dispatch, selectedRecords]);

  const onRowClick = useCallback((record) => {
    if (record.isFolder) {
      setSelectedRecords([]);
      const newCrumbs = [...crumbs];
      newCrumbs.push({
        text: record.name,
        id: record.id,
      });
      setCrumbs(newCrumbs);
    } else {
      setIsDisplay(true);
      setSelectedMaterial(record);
      setShowDrawer(true);
    }
  }, [crumbs]);

  const onSearchClick = useCallback((record) => {
    if (!record) return false;
    const { id, groupId: recordGroupId } = record;
    let groupId = recordGroupId;
    const newCrumbs = [];
    if (record.isFolder) {
      newCrumbs.push({
        text: record.name,
        id: record.id,
      });
    }
    while (groupId) {
      const parent = materials[groupId];
      if (!parent) {
        /*
          Something strange has happened.
          Backend should always return upstream folders
          if user has permission to a material in their downstream
        */
        return false;
      }
      newCrumbs.push({
        text: parent.name,
        id: parent.id,
      });
      groupId = parent.groupId;
    }
    newCrumbs.reverse();
    setCrumbs(INITIAL_CRUMBS.concat(newCrumbs));
    if (record.isFolder) {
      setSelectedRecords([]);
    } else {
      const fullMaterial = materials[id];
      setTimeout(() => {
        setIsDisplay(true);
        setSelectedMaterial(fullMaterial);
        setShowDrawer(true);
      }, 250);
    }
    return true;
  }, [crumbs, materials]);

  const onBack = useCallback(() => {
    const newCrumbs = [...crumbs];
    newCrumbs.pop();
    setCrumbs(newCrumbs);
  }, [crumbs]);

  const onEdit = useCallback(() => setIsDisplay(false), []);
  const onArchive = useCallback(() => {
    ArchiveModal({
      type: selectedMaterial.isAssembly ? 'Assembly' : 'Material',
      content: getRelevantDeleteWarning(materials, [selectedMaterial.id]),
      item: selectedMaterial,
      onOk: async () => {
        if (await dispatch(archiveMaterial(selectedMaterial.id, !selectedMaterial.active))) {
          setSelectedMaterial();
          setShowDrawer(false);
          setIsAssembly();
        }
        return true;
      },
    });
  }, [selectedMaterial, materials, selectedMaterial]);

  const onDelete = useCallback(() => {
    if (!selectedMaterial) return;
    CustomConfirmModal({
      title: `Delete ${selectedMaterial.isAssembly ? 'Assembly' : ''} '${selectedMaterial.name}'?`,
      content: getRelevantDeleteWarning(materials, [selectedMaterial.id]),
      okText: 'Delete',
      cancelText: 'Cancel',
      async onOk() {
        if (await dispatch(deleteMaterial(selectedMaterial.id))) {
          closeDrawer();
        }
      },
    });
  }, [selectedMaterial, closeDrawer, materials]);

  const onBulkChangeClicked = useCallback(({ type, costType = 'material' }) => {
    setBulkChangeType(type);
    setBulkChangeCostType(costType);
  }, []);

  const closeBulkChangeDrawer = useCallback(() => {
    setBulkChangeType();
    setBulkChangeCostType();
  }, []);

  const onBulkChange = useCallback(async (change, changeMode) => {
    if (selectedRecords.length === 0 || !bulkChangeType || !bulkChangeCostType) {
      closeBulkChangeDrawer();
      return;
    }
    if (await dispatch(bulkUpdateMaterials({
      materialIds: selectedRecords.map((record) => record.id),
      type: convertPascalCaseToCamelCase(bulkChangeType),
      costType: bulkChangeCostType,
      change: bulkChangeType === 'Markup' ? change / 100 : change, // Markup uses percent as decimal
      changeMode: bulkChangeType === 'Cost' ? changeMode : undefined,
    }))) {
      closeBulkChangeDrawer();
      setSelectedRecords([]);
    }
  }, [bulkChangeType, bulkChangeCostType, selectedRecords, dispatch, closeBulkChangeDrawer]);

  const onColumnChange = useCallback((newColumns) => {
    setSelectedColumns(new Set(newColumns));
  }, []);

  const onMassArchive = useCallback(() => {
    const selectedIDs = selectedRecords.map((record) => record.id);
    if (selectedIDs.length === 0) return;

    const selectedContainsFolder = selectedRecords.some((record) => record.isFolder);
    const modalTitle = selectedContainsFolder
      ? 'Archiving a folder will inactivate all of its content. Would you like to proceed?'
      : `Archive ${selectedIDs.length} materials?`;

    CustomConfirmModal({
      title: modalTitle,
      content: getRelevantDeleteWarning(materials, selectedIDs),
      okText: 'Archive',
      cancelText: 'Cancel',
      async onOk() {
        if (await dispatch(massArchive(selectedIDs))) {
          setSelectedRecords([]);
        }
      },
    });
  }, [dispatch, selectedRecords, materials]);

  const onMassDelete = useCallback(() => {
    const selectedIDs = selectedRecords.map((record) => record.id);
    if (selectedIDs.length === 0) return;

    CustomConfirmModal({
      title: `Delete ${selectedIDs.length} materials?`,
      content: getRelevantDeleteWarning(materials, selectedIDs),
      okText: 'Delete',
      cancelText: 'Cancel',
      async onOk() {
        if (await dispatch(massDelete(selectedIDs))) {
          setSelectedRecords([]);
        }
      },
    });
  }, [selectedRecords, materials]);

  useEffect(() => {
    dispatch(getMaterials());
    dispatch(getEquipment());
    dispatch(getCardLinks());
    dispatch(getVendors());
    dispatch(getQuickbooksIntegrations());
  }, [dispatch]);

  useEffect(() => {
    const {
      [locationStateId]: ourMaterial,
    } = materials;

    if (ourMaterial) {
      setSelectedMaterial(ourMaterial);
      setIsDisplay(true);
      setTimeout(() => setShowDrawer(true), 250);
    }
  }, [locationStateId, materials]);

  /*
    Materials and sub folders may only live in divisions their parent folder lives in.
    At the root there is no parent, so every division stays available.
  */
  const currentFolderDivisionFilter = useMemo(() => {
    const { id: currentFolderId = null } = crumbs[crumbs.length - 1];
    const { [currentFolderId]: currentFolder } = materials;
    if (!currentFolder?.divisionIds?.length) return undefined;
    return new Set(currentFolder.divisionIds);
  }, [crumbs, materials]);

  const data = useMemo(() => {
    const { id: currentFolderId = null } = crumbs[crumbs.length - 1];
    const folders = [];
    const items = [];
    Object.values(materials).forEach((mat) => {
      if (mat.groupId === currentFolderId
        && isInDivisions(mat, selectedDivisions)
      ) {
        if (mat.isFolder) {
          folders.push(mat);
        } else if (activeFilters.type.has(mat.isAssembly ? 'Assembly' : 'Material')) {
          items.push(mat);
        }
      }
    });
    folders.sort(sortByObjProperty('name'));
    items.sort(sortByObjProperty('name'));
    return folders.concat(items);
  }, [materials, selectedDivisions, crumbs, activeFilters]);

  // Dynamically set default materialLocation to reference default globalMaterialLocation
  const defaultLocation = useMemo(() => {
    const defaultGlobalMaterialLocation = globalMaterialLocations.filter((loc) => loc.locationText === 'DEFAULT');
    if (defaultGlobalMaterialLocation[0]?.id) {
      return [{
        id: 'DEFAULT',
        quantity: 0,
        quantityAllocated: 0,
        isDefault: true,
        globalMaterialLocationsId: defaultGlobalMaterialLocation[0].id,
      }];
    }
    return [{
      id: 'DEFAULT',
      quantity: 0,
      quantityAllocated: 0,
      isDefault: true,
    }];
  }, [globalMaterialLocations]);

  const currentFolderMaterial = useMemo(() => {
    const materialsList = Object.values(materials);
    const { id } = crumbs[crumbs.length - 1];
    const statusFilter = statusFilters?.status;

    return materialsList.filter((material) => {
      const hasDivision = isInDivisions(material, selectedDivisions);
      const matchesStatus = !statusFilter || statusFilter.includes(String(material.active));

      const matchesQuantityThreshold = !statusFilters?.quantityThreshold
      || statusFilters.quantityThreshold.every((filter) => {
        if (filter === 'above') {
          return material.quantity > material.quantityThreshold;
        }
        if (filter === 'under') {
          return material.quantity <= material.quantityThreshold;
        }
        return true;
      });

      const matchesLocations = !statusFilters?.locations
      || (material.locations ?? [])
        .some((loc) => statusFilters.locations.includes(loc.globalMaterialLocationsId)
        || statusFilters.locations.includes(loc.equipmentId));

      const matchesType = !activeFilters?.type
        || activeFilters.type.has(material.isAssembly ? 'Assembly' : 'Material');

      if (
        material.isFolder
      || !hasDivision
      || !matchesStatus
      || !matchesQuantityThreshold
      || !matchesLocations
      || !matchesType
      ) return false;

      return inCurrentFolder(material, materials, id);
    });
  }, [crumbs, materials, selectedDivisions, statusFilters, activeFilters]);

  const currentFolderMaterialWithPaths = useMemo(() => currentFolderMaterial.map((material) => ({
    ...material,
    ...getMaterialPath(material, materials),
  })), [currentFolderMaterial, materials]);

  const onExport = useCallback(async () => {
    if (exportLoading) return;
    setExportLoading(true);
    const missingCostcodeIds = currentFolderMaterialWithPaths
      .filter((material) => material.costcodeId && !costcodeIdMap[material.costcodeId])
      .map((material) => material.costcodeId);
    let fullCostcodeMap = costcodeIdMap;
    if (missingCostcodeIds.length) {
      const newIdMap = await dispatch(getCostcodeByIds(missingCostcodeIds));
      fullCostcodeMap = { ...fullCostcodeMap, ...newIdMap };
    }

    const formattedMaterials = formatExportedMaterial({
      data: currentFolderMaterialWithPaths,
      selectedColumns,
      costcodeMap: fullCostcodeMap,
      equipmentMap,
      globalMaterialLocationsMap,
      vendorMap,
    }).sort(sortByObjProperty('Name'));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(formattedMaterials);
    const headers = Object.keys(formattedMaterials[0] || {});
    // Sets the width of each column based on the header length
    ws['!cols'] = headers.map((header) => ({
      wch: header.length + 2,
    }));

    XLSX.utils.book_append_sheet(wb, ws, 'Materials');
    XLSX.writeFile(wb, 'Materials.xlsx');
    setExportLoading(false);
  }, [
    currentFolderMaterialWithPaths,
    selectedColumns,
    costcodeIdMap,
    equipmentMap,
    globalMaterialLocationsMap,
    exportLoading,
    vendorMap,
  ]);

  if (!Permissions.match('MATERIALS')) {
    history.replace('/dashboard');
    return null;
  }

  return (
    <BreadCrumbContainer
      crumbs={crumbs}
    >
      <MaterialsHeader
        onAdd={onAddClicked}
        onFolderAdd={onAddFolderClicked}
        selected={selectedRecords}
        onBulkChangeClicked={onBulkChangeClicked}
        onColumnChange={onColumnChange}
        onBack={crumbs.length > 1 ? onBack : null}
        onMove={openMoveDrawer}
        onCopy={openCopyDrawer}
        onArchive={onMassArchive}
        onDelete={onMassDelete}
        onSearchClick={onSearchClick}
        filter={(
          <FilterDropdown
            filters={filters}
            activeFilters={activeFilters}
            onFilter={onFilter}
          />
        )}
        filterActive={filterIsAtDefault}
        onExport={onExport}
        exportLoading={exportLoading}
      />
      <MaterialsTable
        onFolderEdit={onFolderEdit}
        data={data}
        onRowClick={onRowClick}
        onSelect={setSelectedRecords}
        selectedColumns={selectedColumns}
        selected={selectedRecords}
        onShare={onShowPerms}
        setStatusFilters={setStatusFilters}
        initialSortFilters={INITIAL_SORT_FILTERS}
      />
      <MaterialDrawer
        key={selectedMaterial ? selectedMaterial.id : `add-${lastCreate}`}
        visible={showDrawer}
        onClose={closeDrawer}
        onSubmit={onAddMaterial}
        isNotDisplay={!isDisplay}
        item={selectedMaterial}
        defaultLocation={defaultLocation}
        onEdit={onEdit}
        onArchive={onArchive}
        onDelete={onDelete}
        onMassUpload={onMassUpload}
        isAssembly={isAssembly}
        divisionFilter={currentFolderDivisionFilter}
      />
      <MaterialBulkChangeDrawer
        visible={bulkChangeType}
        type={bulkChangeType}
        costType={bulkChangeCostType}
        onClose={closeBulkChangeDrawer}
        selected={selectedRecords}
        onSubmit={onBulkChange}
      />
      <MaterialFolderAddDrawer
        key={selectedFolder ? selectedFolder.id : `add-${lastCreate}`}
        item={selectedFolder}
        visible={showFolderAdd}
        onClose={closeFolderAdd}
        onSubmit={onFolderSubmit}
        divisionFilter={currentFolderDivisionFilter}
      />
      <MaterialDestinationDrawer
        loading={loading}
        isCopy={showDestinationDrawer.isCopy}
        visible={showDestinationDrawer.visible}
        onClose={showDestinationDrawer.isCopy ? closeCopyDrawer : closeMoveDrawer}
        selected={selectedRecords}
        onSubmit={showDestinationDrawer.isCopy ? onCopyMaterial : onMoveMaterial}
      />
      <MaterialPermsDrawer
        selectedMaterial={selectedPermsMaterial}
        onClose={onHidePerms}
      />
      <FormDetailView
        visible={formDrawerVisible}
        onClose={() => dispatch(closeFormDrawer())}
        zIndex={1001}
        startOnFields
      />
    </BreadCrumbContainer>
  );
}
